/**
 * AgentEditModal — modal for editing agent profile (name, type, extension, team)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { X, Loader2 } from 'lucide-react';
import { updateAgentProfile } from '../../services/analyticsService';
import { PermissionGate } from '@shared/auth';
import { StandardPermission } from '@shared/auth/permissions';
import useFocusTrap from '../../hooks/useFocusTrap';
import { useAgentNames } from '../shared/AgentName';

const AgentEditModal = ({ agent, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [agentType, setAgentType] = useState('human');
  const [extension, setExtension] = useState('');
  const [team, setTeam] = useState('');
  const [saving, setSaving] = useState(false);
  const modalRef = useRef(null);
  const { forget } = useAgentNames();

  useFocusTrap(modalRef);

  useEffect(() => {
    if (agent) {
      setName(agent.name || agent.agent_id || '');
      setAgentType(agent.agent_type || 'human');
      setExtension(agent.extension || '');
      setTeam(agent.team || '');
    }
  }, [agent]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    const res = await updateAgentProfile(agent.profile_id, {
      name: name.trim(),
      agent_type: agentType,
      extension: extension.trim() || null,
      team: team.trim() || null,
    });
    setSaving(false);
    if (res.success) {
      toast.success('Agent profile updated');
      // Other pages show this agent by name; have them look it up again
      forget(agent.agent_id);
      onSaved();
      onClose();
    } else {
      toast.error(res.error);
    }
  }, [agent, name, agentType, extension, team, forget, onSaved, onClose]);

  if (!agent) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6"
        role="dialog"
        aria-modal="true"
        aria-label="Edit agent profile"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-gray-800">Edit Agent Profile</h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Agent ID (read-only) */}
        <p className="text-xs text-gray-400 mb-4">ID: {agent.agent_id}</p>

        {/* Form */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300"
              placeholder="Agent name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
            <select
              value={agentType}
              onChange={(e) => setAgentType(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
            >
              <option value="human">Human</option>
              <option value="ai">AI</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Extension</label>
            <input
              type="text"
              value={extension}
              onChange={(e) => setExtension(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300"
              placeholder="e.g. 1001"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
            <input
              type="text"
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300"
              placeholder="e.g. Support"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <PermissionGate permission={StandardPermission.Analytics.REPORTS}>
            <button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              <span>{saving ? 'Saving...' : 'Save'}</span>
            </button>
          </PermissionGate>
        </div>
      </div>
    </div>
  );
};

export default AgentEditModal;
